import React, { useState, useMemo, useEffect } from 'react';
import DataTable from '../ui/DataTable';
import { useNavigate } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { Tabs, Tab, Badge } from 'react-bootstrap';

const ApplicationTable = ({ data = [], refreshData, pageSize = 10 }) => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState('active');

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
        setSearchTerm('');
    };

    const columns = [
        {
            key: 'serial',
            label: 'SL No',
            sortable: false,
            render: (_, row, index) => (currentPage - 1) * pageSize + index + 1
        },
        { key: 'applicationNo', label: 'Application No' },
        { key: 'name', label: 'Full Name' },
        {
            key: 'birthDate',
            label: 'Date of Birth',
            render: (value) => formatDate(value)
        },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        {
            key: 'address',
            label: 'Location',
            render: (value) => {
                if (!value) return 'N/A';
                // value is { street, city }
                return `${value.city || ''}`.trim().replace(/^,|,$/g, '');
            }
        },
        { 
            key: 'orderId', 
            label: 'Order Id',
            render: (value) => value || 'N/A' // Handle empty orderId
        },
        {
            key: 'createdAt',
            label: 'Submitted At',
            render: (value) => formatDate(value)
        },
        {
            key: 'paymentTime',
            label: 'Paid At',
            render: (value) => formatDate(value)
        },
    ];

    const actions = [
        {
            label: 'View',
            icon: <FaEye />,
            variant: 'info',
            onClick: (item) => navigate(`/applications/${item._id}`),
            tooltip: 'View application details'
        }
    ];

    // Filter data based on active tab and search term
    const filteredData = useMemo(() => {
        let result = data.filter(item => item.status?.toLowerCase() === activeTab);
        
        if (searchTerm) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            result = result.filter(item =>
                columns.some(col => {
                    if (col.key === 'serial') return false;
                    let value = item[col.key];

                    // If the value is an object, join all its string values
                    if (typeof value === 'object' && value !== null) {
                    value = Object.values(value).join(' ');
                    }

                    return String(value || '').toLowerCase().includes(lowerSearchTerm);
                })
            );
        }
        
        return result;
    }, [data, activeTab, searchTerm]);

    useEffect(() => {
        const totalPages = Math.ceil(filteredData.length / pageSize);
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        } else if (currentPage < 1 && filteredData.length > 0) {
            setCurrentPage(1);
        }
    }, [filteredData, currentPage, pageSize]);

    // Get counts for each status
    const statusCounts = useMemo(() => {
        const counts = {
            active: 0,
            inactive: 0,
        };

        data.forEach(item => {
            const status = item.status?.toLowerCase();
            if (status && counts.hasOwnProperty(status)) {
                counts[status]++;
            }
        });

        return counts;
    }, [data]);

    return (
        <div className="application-table-container">
            <Tabs
                activeKey={activeTab}
                onSelect={handleTabChange}
                className="mb-1"
                id="application-status-tabs"
            >
                <Tab
                    eventKey="active"
                    title={
                        <div className="d-flex align-items-center">
                            Payment Success 
                            <Badge bg="light" text="dark" className="ms-2">
                                {statusCounts.active}
                            </Badge>
                        </div>
                    }
                />
                <Tab
                    eventKey="inactive"
                    title={
                        <div className="d-flex align-items-center">
                            Payment Failed
                            <Badge bg="light" text="dark" className="ms-2">
                                {statusCounts.inactive}
                            </Badge>
                        </div>
                    }
                />
            </Tabs>

            <DataTable
                columns={columns}
                data={filteredData}
                actions={actions}
                searchable={true}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                pagination={true}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                striped={true}
                hover={true}
                bordered={true}
                responsive={true}
                className="mt-3"
                emptyMessage={`No ${activeTab} applications found`}
            />
        </div>
    );
};

ApplicationTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        _id: PropTypes.string.isRequired,
        applicationNo: PropTypes.string,
        name: PropTypes.string,
        birthDate: PropTypes.string,
        address: PropTypes.shape({
            city: PropTypes.string,
        }),
        phone: PropTypes.string,
        email: PropTypes.string,
        status: PropTypes.string,
        orderId: PropTypes.string,
        createdAt: PropTypes.string,
        paymentTime: PropTypes.string
    })),
    refreshData: PropTypes.func,
    pageSize: PropTypes.number
};

ApplicationTable.defaultProps = {
    pageSize: 10
};

export default ApplicationTable;